import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Link2, NotebookPen, Camera, HelpCircle } from "lucide-react";

/**
 * The Blueprint, as a document.
 *
 * This is the artifact that leaves the World on paper, so it is built to be
 * printed and written on: generous margins, one step per block, and a ruled
 * space for notes that survives the printer. Everything MelzAI wrote is
 * replaceable; everything the human ticked or wrote is not.
 */
const Step = ({ step, onToggle, onNotes, busy }) => {
  const [draft, setDraft] = useState(step.notes || "");
  const [editing, setEditing] = useState(false);

  return (
    <motion.article
      className={`blueprint-step rounded-xl border px-5 py-5 md:px-7 md:py-6 ${
        step.done
          ? "border-[var(--stage-gold)]/25 bg-[var(--stage-gold)]/[0.04]"
          : "border-white/8 bg-black/25"
      }`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(step.n * 0.05, 0.4) }}
      data-testid={`blueprint-step-${step.n}`}
    >
      <header className="flex items-start gap-4">
        <button
          type="button"
          onClick={() => onToggle(step)}
          disabled={busy}
          data-testid={`step-done-${step.n}`}
          aria-label={step.done ? "not done after all" : "mark this step done"}
          className={`no-print mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${
            step.done
              ? "border-[var(--stage-gold)] bg-[var(--stage-gold)] text-[#0b0704]"
              : "border-white/25 text-transparent hover:border-[var(--stage-gold)]"
          }`}
        >
          <Check size={13} strokeWidth={3} />
        </button>
        {/* the printer gets a box to tick in pen instead of a button */}
        <span className="print-only mt-1 hidden h-5 w-5 shrink-0 border border-black/60" />

        <div className="min-w-0 flex-1">
          <div className="mono mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] tracking-[0.22em] text-[var(--stage-gold)]/70">
            <span>STEP {step.n}</span>
            {step.window && <span className="text-white/35">{step.window.toUpperCase()}</span>}
            {step.done && <span className="text-[var(--stage-gold)]">STRUCK</span>}
          </div>
          <h3
            className={`serif text-xl leading-snug md:text-2xl ${
              step.done ? "text-white/45 line-through decoration-1" : "text-[#f7ecd8]"
            }`}
            data-testid={`step-title-${step.n}`}
          >
            {step.title}
          </h3>
          {step.body && (
            <p className="mt-2.5 max-w-2xl text-[13.5px] leading-relaxed text-white/55">
              {step.body}
            </p>
          )}
        </div>
      </header>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {step.questions?.length > 0 && (
          <div>
            <p className="tracked mb-2 flex items-center gap-1.5 text-[9px] text-white/35">
              <HelpCircle size={10} /> HOMEWORK BEFORE THIS STEP
            </p>
            <ul className="space-y-1.5" data-testid={`step-questions-${step.n}`}>
              {step.questions.map((q) => (
                <li key={q} className="flex gap-2 text-[12.5px] leading-snug text-white/50">
                  <span className="text-[var(--stage-gold)]/40">—</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          {step.before && (
            <div>
              <p className="tracked mb-1.5 flex items-center gap-1.5 text-[9px] text-white/35">
                <Camera size={10} /> CAPTURE FIRST
              </p>
              <p className="text-[12.5px] leading-snug text-white/50">{step.before}</p>
            </div>
          )}
          {step.links?.length > 0 && (
            <div>
              <p className="tracked mb-1.5 flex items-center gap-1.5 text-[9px] text-white/35">
                <Link2 size={10} /> RESEARCH
              </p>
              <ul className="space-y-1">
                {step.links.map((l) => (
                  <li key={l} className="truncate text-[12px] text-[var(--stage-gold)]/70">
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-white/6 pt-4">
        <p className="tracked mb-2 flex items-center gap-1.5 text-[9px] text-white/30">
          <NotebookPen size={10} /> YOUR NOTES
        </p>
        {editing ? (
          <div className="no-print">
            <textarea
              className="field min-h-[70px] w-full"
              value={draft}
              autoFocus
              placeholder="What actually happened, what it cost, what you would do differently."
              onChange={(e) => setDraft(e.target.value)}
              data-testid={`step-notes-input-${step.n}`}
            />
            <div className="mt-2 flex gap-3">
              <button
                type="button"
                className="btn"
                disabled={busy}
                data-testid={`step-notes-save-${step.n}`}
                onClick={async () => {
                  await onNotes(step, draft);
                  setEditing(false);
                }}
              >
                Keep this
              </button>
              <button
                type="button"
                className="mono text-[10px] text-white/30 hover:text-white/60"
                onClick={() => {
                  setDraft(step.notes || "");
                  setEditing(false);
                }}
              >
                cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`step-notes-${step.n}`}
            className="w-full text-left"
          >
            {step.notes ? (
              <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-white/60">
                {step.notes}
              </p>
            ) : (
              <span className="no-print mono text-[10px] text-white/22 hover:text-[var(--stage-gold)]">
                + write something here
              </span>
            )}
          </button>
        )}
        {/* ruled space so the printed copy is writeable */}
        <div className="print-only hidden">
          <div className="h-6 border-b border-black/25" />
          <div className="h-6 border-b border-black/25" />
          <div className="h-6 border-b border-black/25" />
        </div>
      </div>
    </motion.article>
  );
};

export const BlueprintDoc = ({ blueprint, onToggle, onNotes, busy }) => (
  <section className="blueprint-doc space-y-4" data-testid="blueprint-doc">
    <header className="mb-6">
      <p className="tracked mb-2 text-[9px] text-[var(--stage-gold)]/70">
        THE BLUEPRINT · {blueprint.steps.length} STEPS ·{" "}
        {blueprint.source === "live" ? "WRITTEN LIVE" : "DEMO MODE"}
      </p>
      <h2 className="serif text-3xl font-light leading-tight text-[#f7ecd8] md:text-4xl">
        {blueprint.title}
      </h2>
      {blueprint.summary && (
        <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/55">
          {blueprint.summary}
        </p>
      )}
    </header>

    {blueprint.steps.map((s) => (
      <Step key={s.n} step={s} onToggle={onToggle} onNotes={onNotes} busy={busy} />
    ))}
  </section>
);

export default BlueprintDoc;
